import axios from "axios";

export type FindPlaceCandidate = {
  id?: string;
  formattedAddress?: string;
  addressComponents?: Array<{
    longText?: string;
    shortText?: string;
    types?: string[];
    languageCode?: string;
  }>;
  location?: {
    latitude: number;
    longitude: number;
  };
  iconMaskBaseUri?: string;
  displayName?: {
    text: string;
    languageCode?: string;
  };
};

export type FindPlaceFromTextResponse = {
  places?: FindPlaceCandidate[];
};

export type PlaceAutocompletePrediction = {
  placeId: string;
  text?: { text?: string };
  structuredFormat?: {
    mainText?: { text?: string };
    secondaryText?: { text?: string };
  };
};

type AutocompletePlacesResponse = {
  suggestions?: Array<{
    placePrediction?: Omit<PlaceAutocompletePrediction, "placeId"> & {
      placeId?: string;
    };
  }>;
};

/** Options for `places:searchText` (merged into the JSON body after `textQuery`). */
export type SearchPlacesOptions = Partial<{
  maxResultCount: number;
  languageCode: string;
  regionCode: string;
  includedType: string;
  strictTypeFiltering: boolean;
  locationBias: unknown;
  locationRestriction: unknown;
}>;

/** Options for `places:autocomplete` (merged into the JSON body after `input`). */
export type AutocompletePlacesOptions = Partial<{
  languageCode: string;
  regionCode: string;
  includedPrimaryTypes: string[];
  includeQueryPredictions: boolean;
  locationBias: unknown;
  locationRestriction: unknown;
  sessionToken: string;
}>;

const fieldMaskIDs = {
  searchPlaces:
    "places.displayName,places.formattedAddress,places.location,places.iconMaskBaseUri,places.id,places.addressComponents",
  autocompletePlaces:
    "suggestions.placePrediction.placeId,suggestions.placePrediction.text,suggestions.placePrediction.structuredFormat",
  getPlace:
    "displayName,formattedAddress,location,iconMaskBaseUri,id,addressComponents",
};

const placesHttps = axios.create({
  baseURL: "https://places.googleapis.com/v1",
  headers: {
    "Content-Type": "application/json",
    "X-Goog-Api-Key": process.env.REACT_APP_GOOGLE_API_KEY ?? "",
  },
});

async function searchPlaces(textQuery: string, options?: SearchPlacesOptions) {
  const { data } = await placesHttps<FindPlaceFromTextResponse>({
    method: "post",
    url: "/places:searchText",
    headers: { "X-Goog-FieldMask": fieldMaskIDs.searchPlaces },
    data: { textQuery, maxResultCount: 20, ...options },
  });

  return data;
}

async function autocompletePlaces(
  input: string,
  options?: AutocompletePlacesOptions,
) {
  try {
    const { data } = await placesHttps<AutocompletePlacesResponse>({
      method: "post",
      url: "/places:autocomplete",
      headers: { "X-Goog-FieldMask": fieldMaskIDs.autocompletePlaces },
      data: { input, ...options },
    });

    return (data.suggestions ?? [])
      .map(({ placePrediction }) => placePrediction)
      .filter(
        (prediction): prediction is PlaceAutocompletePrediction =>
          !!prediction?.placeId,
      );
  } catch {
    return [];
  }
}

async function getPlace(placeId: string) {
  const { data } = await placesHttps<FindPlaceCandidate>({
    method: "get",
    url: `/places/${placeId}`,
    headers: { "X-Goog-FieldMask": fieldMaskIDs.getPlace },
  });
  return data;
}

export { searchPlaces, autocompletePlaces, getPlace };
